import { Suspense, lazy } from "react";
import { createBrowserRouter, Navigate, Outlet } from "react-router-dom";

import Header from "../Pages/Components/Headers/Header";
import Footer from "../Pages/Components/Footers/Footer";
import HomePage from "../Pages/HomePage/HomePage";
import {
  setisAdminLogin,
  setisUserLogin,
  setShowSignUpPopup,
  setUserLoggedInData,
} from "../Redux/Slices/User_Slices/User-SLICE";
import { useDispatch, useSelector } from "react-redux";
import UserPopup from "../Pages/Components/Popups/User/UserPopup";
import NoPageFound from "./NoPageFound";
import { LoginWithAuthenticator } from "../Redux/Apis/User-Apis";
import {
  removeLocalStorageJSON,
  setLocalStorageJSON,
} from "../Utils/localStorageFunc";
import toast from "react-hot-toast";
import MyProfile from "../Pages/UserPages/MyProfile/MyProfile";
import ManageLogin from "../Pages/UserPages/ManageLogin/ManageLogin";
import MyTravellers from "../Pages/UserPages/MyTravellers/MyTravellers";
import MyFlightBookings from "../Pages/UserPages/MyFlightBookings/MyFlightBookings";
import UserLayout from "./Layouts/User/UserLayout";
import FlightBooking from "../Pages/UserPages/MyFlightBookings/FlightBooking";
import { Box, CircularProgress, Typography } from "@mui/material";
import MyHotelBookings from "../Pages/UserPages/MyFlightBookings/MyHotelBookings";
import MyWallet from "../Pages/UserPages/MyFlightBookings/MyWallet";
import PendingPayments from "../Pages/UserPages/MyFlightBookings/PendingPayments";
import PrivacyPolicy from "../Pages/Components/Footers/PrivacyPolicy";
import SearchHits from "../Pages/SearchHits/SearchHits";
import "../Assets/Styles/Custom.css";

const FlightResultPage = lazy(() =>
  import("../Pages/ResultPages/Flights/ResultPage")
);
const ReviewPage = lazy(() =>
  import("../Pages/ReviewPages/Flights/ReviewPage")
);
const HotelResultPage = lazy(() =>
  import("../Pages/ResultPages/Hotels/ResultPage")
);
const InternalPage = lazy(() =>
  import("../Pages/ReviewPages/Hotels/InternalPage/InternalPage")
);
const PreviewPage = lazy(() =>
  import("../Pages/ReviewPages/Hotels/PreviewPage/PreviewPage")
);

// Layout Component for Pages with Header and Footer

const Layout = ({ children }) => {
  const dispatch = useDispatch();
  const { showSignUpPopup, isUserLogin, isAdminLogin, selectedPipelineId } = useSelector(
    (state) => state.user
  );

  const closeThePopup = async (type, request) => {
    if (type == "Register") {
      // make autoLogin with authenticator and password — needs the same pipeline_id the account
      // was just created under (SetPassword.jsx's request has no pipeline_id of its own), or the
      // backend's pipeline-scoped user lookup finds nothing and rejects with a validation error.
      let resp = await LoginWithAuthenticator(
        { ...request, pipeline_id: selectedPipelineId },
        false
      );

      if (resp.error == false) {
        toast.success("Registered Successfully", {
          style: {
            fontFamily: `var(--font-family-proxima_nova-semibold)`,
            borderRadius: "10px",
            background: "#333",
            color: "#fff",
          },

          duration: 6000,
        });
        // set token and related things
        dispatch(setUserLoggedInData(resp.data.user));
        removeLocalStorageJSON("AL");
        setLocalStorageJSON("UL", resp.data.token);
        dispatch(setisUserLogin(true));
        dispatch(setisAdminLogin(false));
      } else {
        toast.error("Automatic Login Failed ! , Try To Login Manually");
      }
    }

    dispatch(setShowSignUpPopup(false));
  };

  return (
    <>
      <Header />
      <UserPopup Open={showSignUpPopup} onClose={closeThePopup} />
      <Suspense fallback={<div>Loading...</div>}>{children}</Suspense>
      <Footer />
    </>
  );
};

const ProtectedUserRouteWrapper = () => {
  const { isUserLogin, isAdminLogin , userLoading} = useSelector((state) => state.user);

  if (userLoading == true) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        minHeight="100vh"
        textAlign="center"
      >
        <CircularProgress size={60} />
        <Typography variant="h6" mt={3} color="text.secondary">
          Loading ....
        </Typography>
      </Box>
    );
  }

  // if route is user/mybookings/:id and isuserlogin ,, then direct render <FlightBooking /> else below
  return (isUserLogin || isAdminLogin) ? (
    <UserLayout>
      <Outlet />
    </UserLayout>
  ) : (
    <Navigate to="/" replace />
  );
};

const ProtectedUserOnlyRoute = ({ children }) => {

  const { isUserLogin, isAdminLogin , userLoading} = useSelector((state) => state.user);

  if (userLoading == true) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        minHeight="100vh"
        textAlign="center"
      >
        <CircularProgress size={60} />
        <Typography variant="h6" mt={3} color="text.secondary">
          Loading ....
        </Typography>
      </Box>
    );
  }

  return (isUserLogin || isAdminLogin) ? children : <Navigate to="/" replace />;
};

const router = createBrowserRouter([
  {
    path: "/",
    element: (
      <Layout>
        <HomePage />
      </Layout>
    ),
  },
  {
    path: "/flights",
    element: (
      <Layout>
        <HomePage type="flights" />
      </Layout>
    ),
  },
  {
    path: "/search-hits",
    element: (
      <Layout>
        <SearchHits />
      </Layout>
    ),
  },
  // Hotels
  {
    path: "/hotels",
    element: (
      <Layout>
        <HomePage type="hotels" />
      </Layout>
    ),
  },
  {
    path: "/hotels/listing",
    element: (
      <Layout>
        <HotelResultPage />
      </Layout>
    ),
  },
  {
    path: "/hotels/listing/:id",
    element: (
      <Layout>
        <InternalPage />
      </Layout>
    ),
  },
  {
    path: "/hotels/listing/:id/review",
    element: (
      <Layout>
        <PreviewPage />
      </Layout>
    ),
  },
  // Visa
  {
    path: "/visa",
    element: (
      <Layout>
        <HomePage type="visa" />
      </Layout>
    ),
  },
  // Packages
  {
    path: "/packages",
    element: (
      <Layout>
        <HomePage type="packages" />
      </Layout>
    ),
  },
  {
    path: "/flights/results",
    element: (
      <Layout>
        <FlightResultPage />
      </Layout>
    ),
  },
  {
    path: "/flights/review-details",
    element: (
      <Layout>
        <ReviewPage />
      </Layout>
    ),
  },
  {
    path: "/user",
    children: [
      {
        path: "myflightbookings/:id",
        element: (
          <ProtectedUserOnlyRoute>
            <FlightBooking />
          </ProtectedUserOnlyRoute>
        ),
      },
      {
        element: <ProtectedUserRouteWrapper />, // Wrap the rest with layout
        children: [
          { path: "", element: <Navigate to="myprofile" replace /> },
          { path: "myprofile", element: <MyProfile /> },
          { path: "managelogin", element: <ManageLogin /> },
          { path: "mytravellers", element: <MyTravellers /> },
          { path: "myflightbookings", element: <MyFlightBookings /> },
          { path: "myhotelbookings", element: <MyHotelBookings /> },
          { path: "mywallet", element: <MyWallet /> },
          { path: "mypendingpayments", element: <PendingPayments /> },
          { path: "*", element: <NoPageFound /> },
        ],
      },
    ],
  },

  {
    path: "privacy-policy",
    element:  <Layout>
        <PrivacyPolicy />
      </Layout>
  },
  {
    path: "*",
    element: <NoPageFound />,
  },
  
]);

export default router;
